import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './SubjectRequest.css';
import Layout from '../../../../components/Layout';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Input,
  Button,
  FormControl,
  Select,
  MenuItem,
  Popover,
} from '@mui/material';
import usePagination from '@mui/material/usePagination';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { axiosInstance } from '../../../../utils/tokenUtils';
import FilterIcon from '../../../../assets/Admin/RequestImages/filter-icon.svg';
import ColoredFilterIcon from '../../../../assets/Admin/RequestImages/colored-filter-icon.svg';
import { TableSkeleton } from '../../../../components/Admin/SkeletonLoader/SkeletonLoader';
import SubjectRequestTable from '../../../../components/Admin/Request/SubjectRequestTable/SubjectRequestTable';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`subject-tabpanel-${index}`}
      aria-labelledby={`subject-tab-${index}`}
      sx={{
        flex: 1,
        display: value !== index ? 'none' : 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}

function a11yProps(index) {
  return {
    id: `subject-tab-${index}`,
    'aria-controls': `subject-tabpanel-${index}`,
  };
}

const ApprovalStatusArray = [
  { value: 'all', label: 'All' },
  { value: 'approval_pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const AcademicLevelArray = [
  { value: 'All', label: 'All' },
  { value: 'elementary', label: 'Elementary' },
  { value: 'high_school', label: 'High School' },
  { value: 'middle_school', label: 'Middle School' },
  { value: 'university', label: 'University' },
];

const CreativeLevelArray = [
  { value: 'All', label: 'All' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'professional', label: 'Professional' },
];

// Generate experience array (1-10 + More than 10)
const ExperienceArray = [
  { value: 'All', label: 'All' },
  { value: '1-3', label: '1-3 years' },
  { value: '4-6', label: '4-6 years' },
  { value: '7-9', label: '7-9 years' },
  { value: '10+', label: '10+ years' },
];

// Utility function to format text
const formatText = text => {
  if (!text) return text;
  return text
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const SubjectRequest = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [academicData, setAcademicData] = useState([]);
  const [creativeData, setCreativeData] = useState([]);
  const [value, setValue] = useState(0);
  const [openFilter, setOpenFilter] = useState(false);
  const [level, setLevel] = useState('All');
  const [approvalStatus, setApprovalStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [hasFetchedAcademic, setHasFetchedAcademic] = useState(false);
  const [hasFetchedCreative, setHasFetchedCreative] = useState(false);

  // New state for subjects (lazy-loaded)
  const [academicSubjects, setAcademicSubjects] = useState([]);
  const [creativeSubjects, setCreativeSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('All');

  // Cache flags to avoid refetching subject lists
  const [academicSubjectsLoaded, setAcademicSubjectsLoaded] = useState(false);
  const [creativeSubjectsLoaded, setCreativeSubjectsLoaded] = useState(false);

  // Years of experience filter state
  const [yearsOfExperience, setYearsOfExperience] = useState('All');

  // Pagination states
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Popover anchor element
  const [anchorEl, setAnchorEl] = useState(null);

  // Draft states for filters (used inside popover; applied on "Apply")
  const [draftApprovalStatus, setDraftApprovalStatus] = useState(approvalStatus);
  const [draftLevel, setDraftLevel] = useState(level);
  const [draftSelectedSubject, setDraftSelectedSubject] = useState(selectedSubject);
  const [draftYearsOfExperience, setDraftYearsOfExperience] = useState(yearsOfExperience);

  // Function to get active filters
  const getActiveFilters = () => {
    const filters = [];

    if (approvalStatus !== 'all') {
      const statusLabel =
        ApprovalStatusArray.find(s => s.value === approvalStatus)?.label ||
        formatText(approvalStatus);
      filters.push({
        type: 'approvalStatus',
        label: `Status: ${statusLabel}`,
        value: approvalStatus,
      });
    }

    if (level !== 'All') {
      filters.push({
        type: 'level',
        label: `Level: ${formatText(level)}`,
        value: level,
      });
    }

    if (selectedSubject !== 'All') {
      filters.push({
        type: 'selectedSubject',
        label: `Subject: ${formatText(selectedSubject)}`,
        value: selectedSubject,
      });
    }

    if (yearsOfExperience !== 'All') {
      const experienceLabel =
        ExperienceArray.find(exp => exp.value === yearsOfExperience)?.label || yearsOfExperience;
      filters.push({
        type: 'yearsOfExperience',
        label: `Experience: ${experienceLabel}`,
        value: yearsOfExperience,
      });
    }

    return filters;
  };

  // Handler for removing a filter
  const handleRemoveFilter = filterType => {
    switch (filterType) {
      case 'approvalStatus':
        setApprovalStatus('all');
        break;
      case 'level':
        setLevel('All');
        break;
      case 'selectedSubject':
        setSelectedSubject('All');
        break;
      case 'yearsOfExperience':
        setYearsOfExperience('All');
        break;
      default:
        break;
    }

    // Reset pagination when filters are removed
    setPage(1);
  };

  // Dynamic level options based on selected tab
  const levelOptions = useMemo(() => {
    if (value === 0) {
      // Academic Instructor options
      return [
        { level: 'All' },
        { level: 'Elementary' },
        { level: 'High School' },
        { level: 'Middle School' },
        { level: 'University' },
      ];
    } else {
      // Creative Art Instructor options
      return [
        { level: 'All' },
        { level: 'Beginner' },
        { level: 'Intermediate' },
        { level: 'Professional' },
      ];
    }
  }, [value]);

  // Remove eager subject fetching on mount.
  // Subjects will be fetched lazily when the filter popover is opened.

  // Get appropriate level array based on selected tab
  const getLevelArray = () => {
    return value === 0 ? AcademicLevelArray : CreativeLevelArray;
  };

  // Get current subjects list based on selected tab
  const currentSubjects = useMemo(() => {
    return value === 0 ? academicSubjects : creativeSubjects;
  }, [value, academicSubjects, creativeSubjects]);

  // Resolve selected subject id to avoid effect dependency on subject arrays
  const selectedSubjectId = useMemo(() => {
    if (selectedSubject === 'All') return null;
    const source = value === 0 ? academicSubjects : creativeSubjects;
    const subject = source.find(s => s.title === selectedSubject);
    return subject ? subject.id : null;
  }, [selectedSubject, value, academicSubjects, creativeSubjects]);

  // Helper to lazily load subjects for the active tab
  const ensureSubjectsLoaded = useCallback(async () => {
    try {
      if (value === 0) {
        if (!academicSubjectsLoaded) {
          const academicResponse = await axiosInstance.get(
            '/common/subjects?subject_type=academic'
          );
          setAcademicSubjects(academicResponse.data.results || []);
          setAcademicSubjectsLoaded(true);
        }
      } else {
        if (!creativeSubjectsLoaded) {
          const creativeResponse = await axiosInstance.get(
            '/common/subjects?subject_type=creative'
          );
          setCreativeSubjects(creativeResponse.data.results || []);
          setCreativeSubjectsLoaded(true);
        }
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  }, [value, academicSubjectsLoaded, creativeSubjectsLoaded]);

  // Consolidated effect to fetch subject requests exactly once per dependency change
  useEffect(() => {
    const controller = new AbortController();
    const fetchRequests = async () => {
      try {
        setLoading(true);
        let url = '/admin/subjects/requests';
        const params = new URLSearchParams();

        const teacherType = value === 0 ? 'academic' : 'creative';
        params.append('teacher_type', teacherType);

        // Pagination
        params.append('page', page.toString());
        params.append('page_size', rowsPerPage.toString());

        if (approvalStatus !== 'all') {
          params.append('status', approvalStatus);
        }
        if (level !== 'All') {
          params.append('level_of_teaching', level);
        }
        if (selectedSubjectId) {
          params.append('subject_id', selectedSubjectId);
        }
        if (yearsOfExperience !== 'All') {
          params.append('years_of_experience', yearsOfExperience);
        }
        if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
          params.append('search_query', debouncedSearchTerm.trim());
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

        const { data } = await axiosInstance.get(url, { signal: controller.signal });
        setTotalCount(data?.count || 0);
        if (value === 0) {
          setAcademicData(data.results || []);
          setHasFetchedAcademic(true);
        } else {
          setCreativeData(data.results || []);
          setHasFetchedCreative(true);
        }
      } catch (error) {
        if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
        console.error('Error fetching subject requests:', error);
        if (value === 0) {
          setAcademicData([]);
          setHasFetchedAcademic(true);
        } else {
          setCreativeData([]);
          setHasFetchedCreative(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
    return () => controller.abort();
  }, [
    value,
    approvalStatus,
    level,
    selectedSubjectId,
    yearsOfExperience,
    page,
    rowsPerPage,
    debouncedSearchTerm,
  ]);

  // Initial data load on tab change is covered by the consolidated effect above.

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handleChangeTab = (event, newValue) => {
    if (newValue !== value) {
      setValue(newValue);
      setLevel('All');
      setSelectedSubject('All');
      setYearsOfExperience('All');
      setPage(1);
      // Reset fetch flags when switching tabs to show skeleton
      if (newValue === 0) {
        setHasFetchedAcademic(false);
      } else {
        setHasFetchedCreative(false);
      }
      // Keep other filters (searchTerm and approvalStatus) as they are
    }
  };

  const handleSearchChange = e => setSearchTerm(e.target.value);

  // Event Handlers
  const handleClickToOpenFilter = async event => {
    setAnchorEl(event.currentTarget);
    setOpenFilter(true);
    // Initialize drafts from currently applied filters
    setDraftApprovalStatus(approvalStatus);
    setDraftLevel(level);
    setDraftSelectedSubject(selectedSubject);
    setDraftYearsOfExperience(yearsOfExperience);
    // Lazy-load subjects for the active tab, cached after first load
    await ensureSubjectsLoaded();
  };

  const handleApplyFilter = () => {
    // Apply draft values to active filters
    setApprovalStatus(draftApprovalStatus);
    setLevel(draftLevel);
    setSelectedSubject(draftSelectedSubject);
    setYearsOfExperience(draftYearsOfExperience);
    setOpenFilter(false);
    setAnchorEl(null);
    // Reset pagination when filters are applied
    setPage(1);
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
    setOpenFilter(false);
  };

  const handleClearAll = () => {
    // Clear only draft values; apply on clicking "Apply"
    setDraftLevel('All');
    setDraftApprovalStatus('all');
    setDraftSelectedSubject('All');
    setDraftYearsOfExperience('All');
  };

  // Pagination handlers
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = event => {
    setRowsPerPage(event.target.value);
    setPage(1); // Reset to first page when changing page size
  };

  // Pagination hook
  const { items } = usePagination({
    count: Math.ceil(totalCount / rowsPerPage),
    page: page,
    onChange: handlePageChange,
  });

  return (
    <Layout title="Klass Online | Subject Requests">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] relative bg-[#F9F9F9] rounded-lg mr-[10px] flex flex-col overflow-y-auto">
          <Box className="flex flex-col p-[1rem] gap-3">
            <Box className="flex justify-between items-center">
              <Typography className="request-title">Subject Request</Typography>
              <Box className="flex items-center gap-4 w-[40%]">
                <Input
                  type="search"
                  placeholder="Search by name"
                  onChange={handleSearchChange}
                  value={searchTerm}
                  startAdornment={<SearchIcon className="search-icon-css" />}
                  sx={{
                    width: '100%',
                    height: '40px',
                    background: '#FFF',
                    borderRadius: '56px',
                    padding: '8px 10px',
                    border: '1px solid var(--Stroke, #D5DEE3)',
                    '&:before, &:after': {
                      borderBottom: 'none !important',
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: 'none !important',
                    },
                    '& .MuiInputBase-input': {
                      padding: '3px 5px 3px 5px',
                    },
                  }}
                />
                {openFilter ? (
                  <img
                    src={ColoredFilterIcon}
                    alt="filter"
                    onClick={handleCloseFilter}
                    style={{ width: 25, height: 25, cursor: 'pointer' }}
                  />
                ) : (
                  <img
                    src={FilterIcon}
                    alt="filter"
                    onClick={handleClickToOpenFilter}
                    style={{ width: 25, height: 25, cursor: 'pointer' }}
                  />
                )}
              </Box>
            </Box>
            <Box className="flex flex-col flex-grow h-full overflow-hidden">
              {/* Filter Modal */}
              <Popover
                open={openFilter}
                anchorEl={anchorEl}
                onClose={handleCloseFilter}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  className: 'filter-modal-container',
                }}
              >
                {/* Header */}
                <Box className="filter-modal-header">
                  <Typography className="filter-modal-title">Filters</Typography>
                </Box>

                {/* Scrollable Content */}
                <Box className="filter-modal-content">
                  {/* Grid for filter items */}
                  <Box className="filter-grid">
                    {/* Status Filter */}
                    <Box className="filter-item">
                      <label className="filter-label">Status</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={draftApprovalStatus}
                          onChange={e => setDraftApprovalStatus(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                zIndex: 1500,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          {ApprovalStatusArray.map(status => (
                            <MenuItem
                              key={status.value}
                              value={status.value}
                              className="dropdown-option"
                            >
                              {status.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Level to Teach Filter */}
                    <Box className="filter-item">
                      <label className="filter-label">Level to Teach</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={draftLevel}
                          onChange={e => setDraftLevel(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                zIndex: 1500,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          {getLevelArray().map(levelOption => (
                            <MenuItem
                              key={levelOption.value}
                              value={levelOption.value}
                              className="dropdown-option"
                            >
                              {levelOption.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Subject Filter */}
                    <Box className="filter-item">
                      <label className="filter-label">Subject</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={draftSelectedSubject}
                          onChange={e => setDraftSelectedSubject(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                zIndex: 1500,
                                maxHeight: 200,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          <MenuItem value="All" className="dropdown-option">
                            All
                          </MenuItem>
                          {currentSubjects.map(subject => (
                            <MenuItem
                              key={subject.id}
                              value={subject.title}
                              className="dropdown-option"
                            >
                              {formatText(subject.title)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Years of Experience Filter */}
                    <Box className="filter-item">
                      <label className="filter-label">Years of Experience</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={draftYearsOfExperience}
                          onChange={e => setDraftYearsOfExperience(e.target.value)}
                          displayEmpty
                          className="filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 200,
                                zIndex: 1500,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          {ExperienceArray.map(item => (
                            <MenuItem
                              key={item.value}
                              value={item.value}
                              className="dropdown-option"
                            >
                              {item.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Box>

                {/* Buttons (Sticky Footer) */}
                <Box className="filter-buttons-container">
                  <Button
                    variant="outlined"
                    onClick={handleClearAll}
                    className="filter-clear-button"
                  >
                    Clear All
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilter}
                    className="filter-apply-button"
                  >
                    Apply
                  </Button>
                </Box>
              </Popover>
              <Box className="request-table-container flex-grow h-full flex flex-col overflow-hidden">
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    height: '100%',
                  }}
                >
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', background: '#FFF' }}>
                    <Tabs
                      value={value}
                      onChange={handleChangeTab}
                      aria-label="subject request tabs"
                    >
                      <Tab
                        label="Academic"
                        aria-label="academic instructor tab"
                        sx={{ textTransform: 'none' }}
                        {...a11yProps(0)}
                      />
                      <Tab
                        label="Creative Art"
                        aria-label="creative art instructor tab"
                        sx={{ textTransform: 'none' }}
                        {...a11yProps(1)}
                      />
                    </Tabs>
                  </Box>

                  {/* Active Filters Display */}
                  {getActiveFilters().length > 0 && (
                    <Box className="active-filters-container">
                      {getActiveFilters().map((filter, index) => (
                        <Box key={index} className="filter-card">
                          <Typography className="filter-card-text">{filter.label}</Typography>
                          <CloseIcon
                            className="filter-card-close"
                            onClick={() => handleRemoveFilter(filter.type)}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}

                  {loading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        width: '100%',
                        mt: 2,
                      }}
                    >
                      <TableSkeleton />
                    </Box>
                  ) : (
                    <Box className="flex-grow h-full overflow-hidden">
                      <CustomTabPanel value={value} index={0}>
                        <SubjectRequestTable
                          subjectRequestData={academicData}
                          level={level}
                          approvalStatus={approvalStatus}
                          selectedSubject={selectedSubject}
                          yearsOfExperience={yearsOfExperience}
                          isLoading={loading || !hasFetchedAcademic}
                        />
                      </CustomTabPanel>
                      <CustomTabPanel value={value} index={1}>
                        <SubjectRequestTable
                          subjectRequestData={creativeData}
                          level={level}
                          approvalStatus={approvalStatus}
                          selectedSubject={selectedSubject}
                          yearsOfExperience={yearsOfExperience}
                          isLoading={loading || !hasFetchedCreative}
                        />
                      </CustomTabPanel>
                    </Box>
                  )}

                  {/* Pagination UI */}
                  {!loading && (
                    <Box className="subject-request-pagination-container">
                      <Box className="subject-request-pagination-info">
                        <Typography className="subject-request-pagination-text">Showing</Typography>
                        <Select
                          value={rowsPerPage}
                          onChange={handleRowsPerPageChange}
                          className="subject-request-pagination-select"
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={15}>15</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={30}>30</MenuItem>
                        </Select>
                        <Typography className="subject-request-pagination-text">
                          of {totalCount} Subject Requests
                        </Typography>
                      </Box>
                      <nav className="subject-request-pagination-nav">
                        <ul className="subject-request-pagination-list">
                          {items.map(({ page: itemPage, type, selected, ...item }, index) => {
                            let children = null;

                            if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                              children = (
                                <span className="subject-request-pagination-ellipsis">…</span>
                              );
                            } else if (type === 'page') {
                              children = (
                                <button
                                  type="button"
                                  className={`subject-request-pagination-button ${selected ? 'selected' : ''}`}
                                  {...item}
                                >
                                  {itemPage}
                                </button>
                              );
                            } else {
                              children = (
                                <button
                                  type="button"
                                  className="subject-request-pagination-button"
                                  {...item}
                                >
                                  {formatText(type)}
                                </button>
                              );
                            }

                            return (
                              <li key={index} className="subject-request-pagination-item">
                                {children}
                              </li>
                            );
                          })}
                        </ul>
                      </nav>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default SubjectRequest;
